import { computed, reactive } from 'vue'

// eslint-disable-next-line import/extensions
import { trans } from '@/Translate'
import route from '@/Util/route.js'

import useCheckoutRequest from './useCheckoutRequest.js'

export default function useCheckout(storeDomain, cart, isApi, isPreview) {
  const state = reactive({
    addDiscountWorking: false,
    removeDiscountWorking: false,
    calculateTaxWorking: false,
    updateCartItemWorking: false,
  })

  const working = computed(() => {
    return (
      state.addDiscountWorking ||
      state.removeDiscountWorking ||
      state.calculateTaxWorking ||
      state.validateWorking ||
      state.updateCartItemWorking
    )
  })

  function $route(name, params = {}) {
    if (isApi) {
      return route(
        `spa-api.${name}`,
        {
          cart: cart.id,
          ...params,
        },
        undefined,
      )
    }

    return route(
      name,
      {
        domain: storeDomain,
        cart: cart.id,
        ...params,
      },
      undefined,
    )
  }

  function setLocale(locale) {
    if (!locale) {
      return Promise.reject(trans('lemonsqueezy_checkout.form.locale_required'))
    }

    return new Promise((resolve, reject) => {
      return useCheckoutRequest($route('checkout.locale'), {
        secret: cart.secret,
        cart,
        isFetch: false,
        isApi,
        preserveState: true,
        body: { locale },
        onSuccess: () => resolve(),
        onError: (errors) => reject(errors),
      })
    })
  }

  function saveEmail(email) {
    if (!email) {
      return Promise.reject(trans('lemonsqueezy_checkout.form.email_required'))
    }

    return new Promise((resolve, reject) => {
      useCheckoutRequest($route('checkout.email'), {
        secret: cart.secret,
        isFetch: true,
        body: { email: email },
        onSuccess: () => resolve(),
        onError: (errors) => reject(errors),
      })
    })
  }

  function updateCartItem(payload) {
    return new Promise((resolve, reject) => {
      state.updateCartItemWorking = true

      useCheckoutRequest($route('checkout.update'), {
        isApi,
        secret: cart.secret,
        body: payload,
        onSuccess: () => resolve(),
        onFinish: () => (state.updateCartItemWorking = false),
        onError: () => reject(),
      })
    })
  }

  function validate(payload, paymentService) {
    return new Promise((resolve, reject) => {
      useCheckoutRequest($route('checkout.validate', { type: paymentService }), {
        isApi,
        secret: cart.secret,
        body: payload,
        onSuccess: (data) => resolve(data),
        onError: (errors) => reject(errors),
      })
    })
  }

  function confirmEmail(email) {
    return new Promise((resolve, reject) => {
      state.confirmEmailWorking = true

      useCheckoutRequest($route('checkout.confirm-email'), {
        isApi,
        secret: cart.secret,
        body: { email },
        onSuccess: (data) => resolve(data),
        onFinish: () => (state.confirmEmailWorking = false),
        onError: (errors) => reject(errors),
      })
    })
  }

  function addDiscount(discountCode, service) {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.discount_not_available_in_preview'))
      }

      if (!discountCode) {
        return reject(trans('lemonsqueezy_checkout.form.discount_code_required'))
      }

      state.addDiscountWorking = true

      useCheckoutRequest($route('checkout.add-discount', { type: service }), {
        isApi,
        secret: cart.secret,
        body: { discount_code: discountCode },
        onSuccess: (data) => {
          resolve(data)
        },
        onFinish: () => (state.addDiscountWorking = false),
        onError: (errors) => reject(errors),
      })
    })
  }

  function removeDiscount() {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.remove_discount_not_available_in_preview'))
      }

      state.removeDiscountWorking = true

      useCheckoutRequest($route('checkout.remove-discount'), {
        isApi,
        secret: cart.secret,
        method: 'DELETE',
        onSuccess: (data) => {
          resolve(data)
        },
        onFinish: () => (state.removeDiscountWorking = false),
        onError: (errors) => reject(errors),
      })
    })
  }

  function calculateTax(formData) {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.calculate_tax_not_available_in_preview'))
      }

      if (!formData.country || !formData.postal_code) {
        return reject(trans('lemonsqueezy_checkout.form.country_and_postal_code_required'))
      }

      if (
        formData.country === 'US' &&
        (!formData.line1 || !formData.city || !formData.state || !formData.postal_code)
      ) {
        return reject(trans('lemonsqueezy_checkout.form.address_line_1_city_and_state_required'))
      }

      state.calculateTaxWorking = true

      const bodyParams = {
        country: formData.country,
        postal_code: formData.postal_code,
        tax_number: formData.tax_number,
      }

      if (formData.country === 'US') {
        bodyParams.line1 = formData.line1
        bodyParams.line2 = formData.line2
        bodyParams.city = formData.city
        bodyParams.state = formData.state
      }

      useCheckoutRequest($route('checkout.tax'), {
        isApi,
        secret: cart.secret,
        body: bodyParams,
        onSuccess: (data) => {
          resolve(data)
        },
        onFinish: () => (state.calculateTaxWorking = false),
        onError: (errors) => reject(errors),
      })
    })
  }

  function requestTaxExemptLink() {
    return new Promise((resolve, reject) => {
      useCheckoutRequest($route('checkout.tax-exempt.redirect'), {
        isApi,
        secret: cart.secret,
        method: 'POST',
        onSuccess: (data) => {
          resolve(data)
        },
        onError: (errors) => reject(errors),
      })
    })
  }

  return {
    working,
    $route,
    saveEmail,
    addDiscount,
    removeDiscount,
    calculateTax,
    updateCartItem,
    setLocale,
    validate,
    requestTaxExemptLink,
    confirmEmail,
  }
}
